import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePatient } from '../features/patients/hooks/usePatient';
import { usePatients } from '../features/patients/hooks/usePatients';
import type { Patient } from '../types/admin';
import { Icon } from './Icon';
import styles from './PatientPicker.module.scss';

type PatientPickerProps = {
  readonly value: string | null;
  readonly onChange: (id: string | null) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly id?: string;
};

const RESULT_LIMIT = 20;
const DEBOUNCE_MS = 300;

function patientLabel(patient: Pick<Patient, 'first_name' | 'last_name' | 'display_id' | 'email' | 'id'>): string {
  const name = [patient.first_name, patient.last_name].filter(Boolean).join(' ').trim();
  if (name) return name;
  if (patient.display_id) return String(patient.display_id);
  if (patient.email) return patient.email;
  return patient.id;
}

export function PatientPicker({
  value,
  onChange,
  placeholder,
  disabled = false,
  id,
}: PatientPickerProps) {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const selectedQuery = usePatient(value ?? '');
  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const patient = selectedQuery.data;
    return patient ? patientLabel(patient) : '';
  }, [value, selectedQuery.data]);

  const listQuery = usePatients(
    open ? { search: debouncedQuery.trim() || undefined, page_size: RESULT_LIMIT } : {},
  );
  const results = open ? (listQuery.data?.items ?? []) : [];

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (event.target instanceof Node && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else {
      setQuery('');
    }
  }, [open]);

  function handleSelect(patient: Patient) {
    onChange(patient.id);
    setOpen(false);
  }

  function handleClear(event: React.MouseEvent) {
    event.stopPropagation();
    onChange(null);
    setOpen(false);
  }

  const triggerLabel = value
    ? selectedLabel || t('patientPicker.loading')
    : placeholder || t('patientPicker.placeholder');

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        id={id}
        type="button"
        className={`${styles.trigger} ${value ? styles.triggerActive : ''}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={styles.triggerLabel}>{triggerLabel}</span>
        {value ? (
          <span
            className={styles.clearBtn}
            role="button"
            tabIndex={-1}
            onClick={handleClear}
            aria-label={t('patientPicker.clear')}
          >
            <Icon name="close" size={14} />
          </span>
        ) : (
          <span className={styles.caret}>
            <Icon name="search" size={14} />
          </span>
        )}
      </button>

      {open ? (
        <div className={styles.popover} role="listbox">
          <div className={styles.searchRow}>
            <Icon name="search" size={14} />
            <input
              ref={inputRef}
              className={styles.searchInput}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('patientPicker.searchPlaceholder')}
            />
          </div>
          <div className={styles.results}>
            {listQuery.isLoading ? (
              <div className={styles.muted}>{t('patientPicker.loading')}</div>
            ) : results.length === 0 ? (
              <div className={styles.muted}>{t('patientPicker.noResults')}</div>
            ) : (
              results.map((patient) => (
                <button
                  key={patient.id}
                  type="button"
                  className={`${styles.resultRow} ${patient.id === value ? styles.resultRowActive : ''}`}
                  onClick={() => handleSelect(patient)}
                  role="option"
                  aria-selected={patient.id === value}
                >
                  <span className={styles.resultName}>{patientLabel(patient)}</span>
                  {patient.email ? (
                    <span className={styles.resultMeta}>{patient.email}</span>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
